import { CommonModule } from '@angular/common';
import { Component, HostListener, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { concatMap, finalize, from, switchMap, toArray } from 'rxjs';
import { ImageListItem, Job, PropertyPhoto } from '../../model/tops.models';
import { timestampLabel } from '../../mixins/date-utils';
import { ApiService } from '../../service/api.service';
import { CameraChannel, CameraChannelService } from '../../service/camera-channel.service';
import { ToastService } from '../../service/toast.service';

@Component({
  selector: 'app-property-photos',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  templateUrl: './property-photos.component.html',
  styleUrl: './property-photos.component.css',
})
export class PropertyPhotosComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly api = inject(ApiService);
  private readonly toast = inject(ToastService);
  private readonly cameraChannels = inject(CameraChannelService);

  jobId = '';
  job: Job | null = null;
  imageList: ImageListItem[] = [];
  photos: PropertyPhoto[] = [];
  loading = true;
  uploading = false;
  showIpadUploadOptions = false;
  readonly isIpad = this.detectIpad();
  channel: CameraChannel | null = null;
  heartbeatId: number | null = null;
  openPhotoMenuId: number | string | null = null;
  deletingPhotoId: number | string | null = null;
  displayOrderOptions = Array.from({ length: 101 }, (_, index) => index.toString().padStart(2, '0'));
  editingPhoto: PropertyPhoto | null = null;
  editingPhotoName = '';
  editingPhotoImageType = '';
  editingPhotoDescription = '';
  savingPhotoEdit = false;
  updatingDisplayOrderIds: Array<number | string> = [];

  ngOnInit(): void {
    this.jobId = this.route.snapshot.paramMap.get('jobId') || '';
    this.load();
    this.channel = this.cameraChannels.create(this.jobId);
    this.channel.subscribe((message) => {
      if (message.type === 'photo-saved') this.loadPhotos();
    });
    this.channel.post({ type: 'form-heartbeat' });
    this.heartbeatId = window.setInterval(() => this.channel?.post({ type: 'form-heartbeat' }), 1000);
  }

  ngOnDestroy(): void {
    if (this.heartbeatId) window.clearInterval(this.heartbeatId);
    this.channel?.post({ type: 'form-closing' });
    this.channel?.close();
  }

  @HostListener('document:click')
  closePhotoMenu(): void {
    this.openPhotoMenuId = null;
  }

  load(): void {
    this.api.getJob(this.jobId).subscribe({ next: (job) => { this.job = job; }, error: () => {} });
    this.loadPhotos();
    this.loadImageList();
  }

  loadImageList(): void {
    this.api.getImageList().subscribe({ next: (imageList) => { this.imageList = imageList; }, error: () => {} });
  }

  loadPhotos(): void {
    this.loading = true;
    this.openPhotoMenuId = null;
    this.api.getDisplayPhotos(this.jobId).subscribe({
      next: (photos) => { this.photos = this.sortPhotosByDisplayOrder(photos); this.loading = false; },
      error: (error) => { this.loading = false; this.toast.error('Error', error?.message || 'Failed to load photos.'); },
    });
  }

  upload(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []);
    if (!files.length) return;
    this.showIpadUploadOptions = false;
    this.uploading = true;
    from(files).pipe(
      concatMap((file, index) => {
        const formData = new FormData();
        formData.append('photo', file);
        formData.append('label', files.length > 1 ? `${timestampLabel('Photo')} ${index + 1}` : timestampLabel('Photo'));
        return this.api.uploadPhoto(this.jobId, formData);
      }),
      finalize(() => { this.uploading = false; input.value = ''; }),
    ).subscribe({
      complete: () => { this.toast.success('Success', `${files.length} photo${files.length === 1 ? '' : 's'} uploaded successfully.`); this.loadPhotos(); },
      error: (error) => { this.toast.error('Error', error?.message || 'Failed to upload photos.'); this.loadPhotos(); },
    });
  }

  deletePhoto(photo: PropertyPhoto): void {
    if (!window.confirm(`Delete ${photo.label || 'this photo'}?`)) return;
    this.api.deletePhoto(photo.id).subscribe({
      next: () => { this.toast.success('Success', 'Photo deleted successfully.'); this.loadPhotos(); },
      error: (error) => this.toast.error('Error', error?.message || 'Failed to delete photo.'),
    });
  }

  isUnsyncedPhoto(photo: PropertyPhoto): boolean {
    return photo.photoUrl.startsWith('data:image/') && !photo.documentUid && !photo.s3Key;
  }

  togglePhotoMenu(event: Event, photo: PropertyPhoto): void {
    event.stopPropagation();
    if (!this.isUnsyncedPhoto(photo)) return;
    this.openPhotoMenuId = this.openPhotoMenuId === photo.id ? null : photo.id;
  }

  deleteUnsyncedPhoto(event: Event, photo: PropertyPhoto): void {
    event.stopPropagation();
    if (!this.isUnsyncedPhoto(photo) || this.deletingPhotoId === photo.id) return;
    this.openPhotoMenuId = null;
    this.deletingPhotoId = photo.id;
    this.api.deleteLocalPendingPhoto(photo.id).subscribe({
      next: () => {
        this.deletingPhotoId = null;
        this.toast.success('Success', 'Photo deleted successfully.');
        this.loadPhotos();
        this.channel?.post({ type: 'photo-saved' });
      },
      error: (error) => {
        this.deletingPhotoId = null;
        this.toast.error('Error', error?.message || 'Failed to delete photo.');
      },
    });
  }

  openPhotoEdit(event: Event, photo: PropertyPhoto): void {
    event.stopPropagation();
    if (!this.isUnsyncedPhoto(photo) || this.savingPhotoEdit) return;
    this.openPhotoMenuId = null;
    this.editingPhoto = photo;
    this.editingPhotoName = photo.label || '';
    this.editingPhotoImageType = photo.imageType || '';
    this.editingPhotoDescription = photo.description || '';
  }

  savePhotoEdit(): void {
    if (!this.editingPhoto || this.savingPhotoEdit) return;
    this.savingPhotoEdit = true;
    this.api.updatePhoto(this.jobId, this.editingPhoto.id, {
      label: this.editingPhotoName.trim() || null,
      description: this.editingPhoto.description ?? null,
      imageType: this.editingPhotoImageType.trim() || null,
      displayOrder: this.editingPhoto.displayOrder ?? null,
    }).subscribe({
      next: (updated) => {
        this.savingPhotoEdit = false;
        this.photos = this.sortPhotosByDisplayOrder(this.photos.map((photo) => photo.id === updated.id ? updated : photo));
        this.closePhotoEdit();
        this.channel?.post({ type: 'photo-saved' });
        this.toast.success('Success', 'Photo details updated successfully.');
      },
      error: (error) => {
        this.savingPhotoEdit = false;
        this.toast.error('Error', error?.message || 'Failed to update photo details.');
      },
    });
  }

  closePhotoEdit(): void {
    if (this.savingPhotoEdit) return;
    this.editingPhoto = null;
    this.editingPhotoName = '';
    this.editingPhotoImageType = '';
    this.editingPhotoDescription = '';
  }


  photoSource(photo: PropertyPhoto): string {
    return photo.photoUrl.startsWith('http') ? photo.photoUrl : photo.photoUrl;
  }

  imageTypeLabel(photo: PropertyPhoto): string {
    const imageType = photo.imageType?.trim();
    if (!imageType) return '';
    return this.imageList.find((image) => image.value === imageType)?.label || imageType;
  }

  updatePhotoDisplayOrder(photo: PropertyPhoto, displayOrder: string): void {
    if (this.updatingDisplayOrderIds.includes(photo.id) || (photo.displayOrder || '00') === displayOrder) return;

    const nextOrder = this.displayOrderNumber(displayOrder);
    const currentOrder = this.displayOrderNumber(photo.displayOrder);
    const photosToShift = this.photosToShiftForMove(photo.id, currentOrder, nextOrder);
    this.updatingDisplayOrderIds = [photo.id, ...photosToShift.map((photoToShift) => photoToShift.photo.id)];

    from(photosToShift).pipe(
      concatMap((photoToShift) => this.api.updatePhoto(this.jobId, photoToShift.photo.id, this.photoPayload(photoToShift.photo, photoToShift.displayOrder))),
      toArray(),
      switchMap((updatedPhotos) => this.api.updatePhoto(this.jobId, photo.id, this.photoPayload(photo, displayOrder || null)).pipe(
        switchMap((updatedPhoto) => from([[...updatedPhotos, updatedPhoto]])),
      )),
    ).subscribe({
      next: (updatedPhotos) => {
        this.updatingDisplayOrderIds = [];
        this.photos = this.sortPhotosByDisplayOrder(this.photos.map((currentPhoto) => updatedPhotos.find((updated) => updated.id === currentPhoto.id) || currentPhoto));
        this.channel?.post({ type: 'photo-saved' });
      },
      error: (error) => {
        this.updatingDisplayOrderIds = [];
        this.toast.error('Error', error?.message || 'Failed to update display order.');
        this.loadPhotos();
      },
    });
  }

  private photosToShiftForMove(photoId: number | string, currentOrder: number | null, nextOrder: number | null): Array<{ photo: PropertyPhoto; displayOrder: string | null }> {
    if (nextOrder === null) return [];
    const movingUp = currentOrder === null || nextOrder < currentOrder;
    const photosToShift = this.photos
      .filter((photo) => photo.id !== photoId)
      .map((photo) => ({ photo, order: this.displayOrderNumber(photo.displayOrder) }))
      .filter((item): item is { photo: PropertyPhoto; order: number } => item.order !== null)
      .filter((item) => movingUp
        ? item.order >= nextOrder && (currentOrder === null || item.order < currentOrder)
        : item.order > currentOrder && item.order <= nextOrder)
      .sort((left, right) => movingUp ? right.order - left.order : left.order - right.order);

    return photosToShift.map((item) => ({
      photo: item.photo,
      displayOrder: this.shiftedDisplayOrder(item.order, movingUp ? 1 : -1),
    }));
  }

  private shiftedDisplayOrder(order: number, offset: number): string | null {
    const nextOrder = order + offset;
    if (nextOrder <= 0 || nextOrder > 100) return '00';
    return String(nextOrder).padStart(2, '0');
  }

  private photoPayload(photo: PropertyPhoto, displayOrder: string | null): Pick<PropertyPhoto, 'label' | 'description' | 'imageType' | 'displayOrder'> {
    return {
      label: photo.label ?? null,
      description: photo.description ?? null,
      imageType: photo.imageType ?? null,
      displayOrder,
    };
  }

  private sortPhotosByDisplayOrder(photos: PropertyPhoto[]): PropertyPhoto[] {
    return photos
      .map((photo, index) => ({ photo, index }))
      .sort((left, right) => {
        const leftOrder = this.displayOrderNumber(left.photo.displayOrder);
        const rightOrder = this.displayOrderNumber(right.photo.displayOrder);
        if (leftOrder === null && rightOrder === null) return left.index - right.index;
        if (leftOrder === null) return 1;
        if (rightOrder === null) return -1;
        return leftOrder - rightOrder || left.index - right.index;
      })
      .map(({ photo }) => photo);
  }

  private displayOrderNumber(displayOrder?: string | null): number | null {
    if (!displayOrder) return null;
    const numberValue = Number(displayOrder);
    return Number.isFinite(numberValue) && numberValue > 0 ? numberValue : null;
  }

  toggleIpadUploadOptions(): void {
    if (this.uploading) return;
    this.showIpadUploadOptions = !this.showIpadUploadOptions;
  }

  private detectIpad(): boolean {
    const userAgent = navigator.userAgent || '';
    const platform = navigator.platform || '';
    return /iPad/i.test(userAgent) || (platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  }
}
